import React, { forwardRef, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';

import { Skeleton } from '@mui/material';
import classNames from 'classnames';
import { CreativeType } from '../../types';
import css from './CreativesLayout.css';
import CreativesGrid from '../creativesGrid/CreativesGrid';
import Select from '../../../components/widgets/MuiSelect';
import { AutoCompleteOptionType } from '../../../components/molecules/autocompleteTypes';
import { useUrlParams } from '../../../utils/useUrlParamsHook';
import Icon, { IconVariants } from '../../../components/foundations/Icon';
import { ALL_METRICS_OPTIONS } from '../../metricsUtils';
import { useLocalStorageState } from '../../../utils/customHooks';
import Tooltip from '../../../components/molecules/Tooltip';
import { GalleryContext, LayoutConfig } from '../../consts';
import Button from '../../../components/atoms/Button';

export const SORT_SELECT_LABEL = 'STATIC.PAGES.CREATIVE_GALLERY.GRID_HEADER.SORT_SELECT_LABEL';
export const CREATIVES_AMOUNT_TEXT = 'STATIC.PAGES.CREATIVE_GALLERY.GRID_HEADER.CREATIVES_AMOUNT_TEXT';
export const LOCAL_STORAGE_LAYOUT_KEY = 'gallery_layout';

export const sortCreativeByMetric = (sortBy, isDescSort, isCohort) => {
    const sortKey = isCohort ? 'cohorts' : 'metrics';
    return (firstCreative, secondCreative) =>
        (isDescSort ? 1 : -1) * (secondCreative[sortKey][sortBy] - firstCreative[sortKey][sortBy]);
};

const CreativesLayout = forwardRef(
    (
        {
            creatives = [],
            onCreativeClick,
            isLoading,
            loadingSections,
            selectedMetrics = [],
            metricsOptions = [],
            onSortChange: onSortChangeProp = () => {},
            selectedCohorts = [],
        },
        ref
    ) => {
        const { getParamsObject, setParamsObject } = useUrlParams();
        const [sortBy, setSortBy] = useState(() => {
            const { sortBy: urlSortBy } = getParamsObject();

            if (urlSortBy) return urlSortBy;

            return selectedMetrics[0]?.value || selectedCohorts[0]?.value || null;
        });

        const [isDescSort, setIsDescSort] = useState(() => {
            const { isDescSort: urlIsDescSort } = getParamsObject();
            return urlIsDescSort ?? true;
        });

        const [layout, setLayout] = useLocalStorageState(LOCAL_STORAGE_LAYOUT_KEY, LayoutConfig.GALLERY.type);

        const sortByOptions = useMemo(() => metricsOptions.concat(selectedCohorts), [metricsOptions, selectedCohorts]);

        const onSortChange = value => {
            onSortChangeProp(value);
            setSortBy(value);
            setParamsObject({ sortBy: value });
        };

        useEffect(() => {
            if (!sortBy) return;

            const sortByMetrics = selectedMetrics.concat(selectedCohorts);

            if (!sortByMetrics.length || (sortBy && sortByMetrics.some(({ value }) => value === sortBy))) return;

            onSortChange(sortByMetrics[0].value);
        }, [selectedMetrics, selectedCohorts]);

        const sortedCreatives = useMemo(() => {
            if (!sortBy || !creatives?.length) return creatives || [];

            const isCohort = !ALL_METRICS_OPTIONS.some(metric => metric.value === sortBy);
            return [...creatives].sort(sortCreativeByMetric(sortBy, isDescSort, isCohort));
        }, [sortBy, isDescSort, creatives]);

        const onSortDirectionChange = () => {
            const newDescValue = !isDescSort;
            setIsDescSort(newDescValue);
            setParamsObject({ isDescSort: newDescValue });
        };

        const onLayoutChange = layoutType => {
            setLayout(layoutType);
            setParamsObject({ layout: layoutType });
        };

        return (
            <>
                {isLoading ? (
                    <Skeleton variant="text" className={css.skeleton} />
                ) : (
                    <div className={css.header}>
                        <Translate id={CREATIVES_AMOUNT_TEXT} data={{ amount: creatives.length }} />
                        <div className={css.preferencesSection}>
                            <div className={css.layout}>
                                {Object.values(LayoutConfig).map(option => (
                                    <Tooltip
                                        key={option.tooltip}
                                        wrapperClass={css.tooltipContainer}
                                        titleTranslationKey={option.tooltip}
                                    >
                                        <div
                                            key={option.type}
                                            className={classNames(css.layoutButton, css.preferenceButton, {
                                                [css.active]: layout === option.type,
                                            })}
                                        >
                                            <Icon name={option.icon} onClick={() => onLayoutChange(option.type)} />
                                        </div>
                                    </Tooltip>
                                ))}
                            </div>
                            <div className={classNames(css.sortDirectionContainer, css.preferenceButton)}>
                                <Icon
                                    name={isDescSort ? 'sort-descending-2' : 'sort-ascending-2'}
                                    onClick={onSortDirectionChange}
                                />
                            </div>
                            <Select
                                value={sortBy}
                                onChange={onSortChange}
                                options={sortByOptions}
                                label={SORT_SELECT_LABEL}
                            />
                        </div>
                    </div>
                )}
                <CreativesGrid
                    ref={ref}
                    creatives={sortedCreatives}
                    onCreativeClick={onCreativeClick}
                    isLoading={isLoading}
                    loadingSections={loadingSections}
                    selectedMetrics={selectedMetrics}
                    selectedCohorts={selectedCohorts}
                    layout={layout}
                />
            </>
        );
    }
);

CreativesLayout.propTypes = {
    creatives: PropTypes.arrayOf(PropTypes.shape(CreativeType)),
    onCreativeClick: PropTypes.func,
    selectedMetrics: PropTypes.arrayOf(AutoCompleteOptionType),
    metricsOptions: PropTypes.arrayOf(AutoCompleteOptionType),
    isLoading: PropTypes.bool.isRequired,
    loadingSections: PropTypes.arrayOf(PropTypes.string),
    onSortChange: PropTypes.func,
    selectedCohorts: PropTypes.arrayOf(AutoCompleteOptionType),
};

export default CreativesLayout;
